// Angular
import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, DOCUMENT, PLATFORM_ID, inject } from '@angular/core';
// Lodash
import { get } from 'lodash';
// Environments
import { environment } from '@environment';

@Component({
  selector: 'app-google-analytics',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export class GoogleAnalyticsComponent {

  private readonly platformId = inject<Object>(PLATFORM_ID);
  private readonly document = inject<Document>(DOCUMENT);

  /**
   * Creates an instance of GoogleAnalyticsComponent.
   * @memberof GoogleAnalyticsComponent
   */
  constructor() {
    const isGoogleAnalyticsEnabled: boolean = get(
      environment,
      'googleAnalytics.isEnabled',
      false,
    );

    if (isGoogleAnalyticsEnabled && isPlatformBrowser(this.platformId)) {
      try {
        this.injectGA4Script();
      } catch (error) {
        console.error('Error while loading Google-Analytic script', error);
      }
    }
  }

  /**
   * Google Analytics 4
   * @memberof GoogleAnalyticsComponent
   */
  private injectGA4Script(): void {
    const measurementId = get(environment, 'googleAnalytics.measurementId');

    if (!measurementId) {
      console.error('Google Analytics Measurement ID is not configured.');
      return;
    }

    const gaTagURL: string = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

    const scriptGtag = this.document.createElement('script') as HTMLScriptElement;
    scriptGtag.src = gaTagURL;
    scriptGtag.async = true;

    scriptGtag.onerror = () => {
      (window as any).__gaUnavailable = true;

      if ((window as any).dataLayer) {
        (window as any).dataLayer.length = 0;
      }
    };

    this.document.head.appendChild(scriptGtag);

    const scriptInit = this.document.createElement('script') as HTMLScriptElement;

    const scriptBody = this.document.createTextNode(`
      window.dataLayer = window.dataLayer || [];

      function gtag() {
        if (window.__gaUnavailable) return;

        dataLayer.push(arguments);

        if (dataLayer.length > 500) {
          dataLayer.splice(0, dataLayer.length - 500);
        }
      }

      gtag('js', new Date());

      // Deny consent by default for all categories
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'denied',
        'wait_for_update': 500
      });

      gtag('config', '${measurementId}');
    `);

    scriptInit.appendChild(scriptBody);
    this.document.head.appendChild(scriptInit);
  }
}